import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BellRing, Inbox, Loader2, Mail, Send, Sparkles, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { PageContainer } from '../../../common/components/PageContainer';
import { PillTabs } from '../../../components/design-system/admin/PillTabs';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { StatusBadge, StatusTone } from '../../../components/design-system/admin/StatusBadge';
import { SettingsField, SettingsGroup, SettingsToggleRow } from '../../../components/design-system/common/SettingsPanel';
import { readApiError } from '../../../common/utils/api-error';
import { ArdiChannel, useArdiApi } from '../../../common/hooks/use-ardi-api.hook';
import { BriefLanguage, BriefPreview, BriefRunSummary, BriefSettings, BriefSettingsInput, useDecisionsApi } from '../../../common/hooks/use-decisions-api.hook';
import { CHANNEL_ICONS, channelPhase } from '../../../modules/vendor/channels/channel-presentation';
import { pad, timezones } from '../../../modules/vendor/routines/routine-format';
import { ChatPreview, EmailPreview } from '../../../modules/vendor/decisions/BriefPreview';

type Draft = Required<BriefSettingsInput>;
type Tab = 'whatsapp' | 'telegram' | 'email';

const DAYS = [1, 2, 3, 4, 5, 6, 0];
const LANGUAGES: BriefLanguage[] = ['auto', 'en', 'fr', 'es', 'ar'];
const STATUS_TONE: Record<BriefRunSummary['status'], StatusTone> = { sent: 'success', partial: 'warning', failed: 'danger', skipped: 'neutral' };

const draftOf = (s: BriefSettings): Draft => ({
  enabled: s.enabled,
  hour: s.hour,
  minute: s.minute,
  timezone: s.timezone,
  weekdays: [...s.weekdays].sort(),
  channelUuids: [...s.channelUuids].sort(),
  email: s.email,
  language: s.language,
  aiLine: s.aiLine,
  skipWhenQuiet: s.skipWhenQuiet,
});

const dayShort = (day: number, lang: string) => {
  // 2026-09-27 is a Sunday.
  try {
    return new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 8, 27 + day, 12)));
  } catch {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day];
  }
};

/**
 * The morning brief: when it goes, where (the vendor's own Ardi channels and
 * email), and — beside the settings — exactly what it would say now, drawn
 * the way WhatsApp, Telegram and the inbox will show it.
 */
export const MorningBriefPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const navigate = useNavigate();
  const api = useDecisionsApi();
  const ardi = useArdiApi();

  const [saved, setSaved] = useState<BriefSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [channels, setChannels] = useState<ArdiChannel[] | null>(null);
  const [preview, setPreview] = useState<BriefPreview | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [tab, setTab] = useState<Tab>('whatsapp');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.getBriefSettings()
      .then((s) => {
        setSaved(s);
        setDraft(draftOf(s));
      })
      .catch(() => toast.error(t('DECISIONS.BRIEF.LOAD_FAILED')));
    ardi.listChannels().then((r) => setChannels(r.channels)).catch(() => setChannels([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewLang = draft && draft.language !== 'auto' ? draft.language : undefined;
  const loadPreview = useCallback(() => {
    setPreviewFailed(false);
    return api.previewBrief(previewLang).then(setPreview).catch(() => setPreviewFailed(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewLang]);

  useEffect(() => {
    if (saved) void loadPreview();
  }, [saved, loadPreview]);

  const dirty = useMemo(() => !!saved && !!draft && JSON.stringify(draftOf(saved)) !== JSON.stringify(draft), [saved, draft]);
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));

  const save = async (): Promise<boolean> => {
    if (!draft) return false;
    setSaving(true);
    try {
      const s = await api.updateBriefSettings(draft);
      setSaved(s);
      setDraft(draftOf(s));
      toast.success(t('DECISIONS.BRIEF.SAVED'));
      return true;
    } catch (err) {
      const body = await readApiError(err);
      toast.error(body.message ? String(body.message) : t('DECISIONS.BRIEF.SAVE_FAILED'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  /** What is on screen is what is tested: unsaved changes are saved first. */
  const sendTest = async () => {
    if (dirty && !(await save())) return;
    setTesting(true);
    try {
      const run = await api.sendTestBrief();
      setSaved((s) => (s ? { ...s, lastRun: run } : s));
      if (run.status === 'sent') toast.success(t('DECISIONS.BRIEF.TEST_SENT'));
      else if (run.status === 'partial') toast.warning(t('DECISIONS.BRIEF.TEST_PARTIAL'));
      else toast.error(t('DECISIONS.BRIEF.TEST_FAILED'));
    } catch (err) {
      const body = await readApiError(err);
      toast.error(body.message ? String(body.message) : t('DECISIONS.BRIEF.TEST_FAILED'));
    } finally {
      setTesting(false);
    }
  };

  // Channels the brief can use: connected ones (the rest are set up on the Channels page).
  const usable = (channels ?? []).filter((c) => c.status !== 'pending');
  const chosen = new Set(draft?.channelUuids ?? []);
  const toggleChannel = (uuid: string, on: boolean) =>
    set({ channelUuids: on ? [...(draft?.channelUuids ?? []), uuid].sort() : (draft?.channelUuids ?? []).filter((u) => u !== uuid) });
  const firstChosen = (kind: string) => usable.find((c) => c.kind === kind && chosen.has(c.uuid));

  const nextRun = saved?.enabled && saved.nextRunAt
    ? new Date(saved.nextRunAt).toLocaleString(lang, { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: saved.timezone })
    : null;

  const last = saved?.lastRun ?? null;
  const windowClosed = last?.deliveries.some((d) => d.note === 'whatsapp_window');

  const tabs = [
    { value: 'whatsapp' as Tab, label: t('DECISIONS.BRIEF.TAB.WHATSAPP') },
    { value: 'telegram' as Tab, label: t('DECISIONS.BRIEF.TAB.TELEGRAM') },
    { value: 'email' as Tab, label: t('DECISIONS.BRIEF.TAB.EMAIL') },
  ];

  return (
    <PageContainer breadcrumbs={[{ label: t('AGENTS.NAV.HOME', 'Home'), href: '/home/dashboard' }, { label: t('R2R.NAV.BRIEF') }]}>
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6" data-testid="brief-page">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-medium tracking-tight">{t('DECISIONS.BRIEF.TITLE')}</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('DECISIONS.BRIEF.SUBTITLE')}</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/home/decisions')}>
            <Inbox />
            {t('DECISIONS.BRIEF.OPEN_DECISIONS')}
          </Button>
        </div>

        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)]">
          {/* ── Settings ── */}
          <div className="flex flex-col gap-6">
            <Card className="gap-0 py-0" data-testid="brief-settings">
              {!draft ? (
                <div className="flex flex-col gap-4 p-6">
                  <Skeleton className="h-6 w-1/2" />
                  <Skeleton className="h-24" />
                  <Skeleton className="h-24" />
                </div>
              ) : (
                <div className="flex flex-col gap-7 p-6">
                  <SettingsToggleRow
                    id="brief-enabled"
                    label={t('DECISIONS.BRIEF.ON')}
                    description={nextRun ? t('DECISIONS.BRIEF.NEXT', { when: nextRun }) : t('DECISIONS.BRIEF.ON_DESC')}
                    checked={draft.enabled}
                    onCheckedChange={(v) => set({ enabled: v })}
                  />

                  <SettingsGroup title={t('DECISIONS.BRIEF.WHEN')}>
                    <div className="flex flex-wrap items-end gap-3">
                      <SettingsField label={t('DECISIONS.BRIEF.TIME')} htmlFor="brief-time">
                        <Input
                          id="brief-time"
                          type="time"
                          className="w-32"
                          value={`${pad(draft.hour)}:${pad(draft.minute)}`}
                          onChange={(e) => {
                            const [h, m] = e.target.value.split(':').map(Number);
                            if (!Number.isNaN(h) && !Number.isNaN(m)) set({ hour: h, minute: m });
                          }}
                        />
                      </SettingsField>
                      <SettingsField label={t('DECISIONS.BRIEF.TIMEZONE')} htmlFor="brief-tz" className="min-w-48 flex-1">
                        <NativeSelect id="brief-tz" value={draft.timezone} onChange={(e) => set({ timezone: e.target.value })}>
                          {timezones(draft.timezone).map((z) => (
                            <NativeSelectOption key={z} value={z}>{z.replace(/_/g, ' ')}</NativeSelectOption>
                          ))}
                        </NativeSelect>
                      </SettingsField>
                    </div>
                    <SettingsField label={t('DECISIONS.BRIEF.DAYS')}>
                      <ToggleGroup
                        type="multiple"
                        variant="outline"
                        value={draft.weekdays.map(String)}
                        onValueChange={(v) => set({ weekdays: v.map(Number).sort() })}
                        className="flex-wrap justify-start"
                        aria-label={t('DECISIONS.BRIEF.DAYS') as string}
                      >
                        {DAYS.map((d) => (
                          <ToggleGroupItem key={d} value={String(d)} className="min-w-12 px-2.5 text-xs capitalize">
                            {dayShort(d, lang)}
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                    </SettingsField>
                  </SettingsGroup>

                  <SettingsGroup title={t('DECISIONS.BRIEF.WHERE')} description={t('DECISIONS.BRIEF.WHERE_DESC')}>
                    <div className="flex flex-col gap-2" data-testid="brief-channels">
                      {channels === null && <Skeleton className="h-11" />}
                      {usable.map((c) => {
                        const Icon = CHANNEL_ICONS[c.kind];
                        const phase = channelPhase(c);
                        const id = `brief-channel-${c.uuid}`;
                        const hint =
                          phase === 'error' ? t('DECISIONS.BRIEF.CHANNEL_ERROR')
                            : c.kind === 'webhook' ? (c.info?.outboundUrl ? t('DECISIONS.BRIEF.WEBHOOK_READY') : t('DECISIONS.BRIEF.NOTE.NO_OUTBOUND'))
                              : c.links.length ? t('DECISIONS.BRIEF.CHATS', { count: c.links.length })
                                : t('DECISIONS.BRIEF.NOTE.NO_CHAT');
                        return (
                          <label key={c.uuid} htmlFor={id} className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 hover:bg-accent">
                            <Checkbox id={id} className="mt-0.5" checked={chosen.has(c.uuid)} onCheckedChange={(v) => toggleChannel(c.uuid, v === true)} disabled={phase === 'error'} />
                            <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm">{c.name}</span>
                              <span className={cn('block text-xs', phase === 'error' ? 'text-destructive' : 'text-muted-foreground')}>{hint}</span>
                              {c.kind === 'whatsapp' && chosen.has(c.uuid) && (
                                <span className="mt-1 block text-xs text-muted-foreground">{t('DECISIONS.BRIEF.WHATSAPP_HINT')}</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                      {channels !== null && !usable.length && (
                        <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed p-3">
                          <span className="text-xs text-muted-foreground">{t('DECISIONS.BRIEF.NO_CHANNELS')}</span>
                          <Button variant="outline" size="sm" onClick={() => navigate('/home/channels')}>{t('DECISIONS.BRIEF.CONNECT')}</Button>
                        </div>
                      )}
                    </div>
                    <SettingsToggleRow
                      id="brief-email"
                      icon={<Mail />}
                      label={t('DECISIONS.BRIEF.EMAIL')}
                      description={t('DECISIONS.BRIEF.EMAIL_DESC')}
                      checked={draft.email}
                      onCheckedChange={(v) => set({ email: v })}
                    />
                    <SettingsToggleRow
                      id="brief-bell"
                      icon={<BellRing />}
                      label={t('DECISIONS.BRIEF.BELL')}
                      description={t('DECISIONS.BRIEF.BELL_DESC')}
                      checked
                      disabled
                      onCheckedChange={() => undefined}
                    />
                  </SettingsGroup>

                  <SettingsGroup title={t('DECISIONS.BRIEF.WORDS')}>
                    <SettingsField label={t('DECISIONS.BRIEF.LANGUAGE')} htmlFor="brief-lang" className="max-w-xs">
                      <NativeSelect id="brief-lang" value={draft.language} onChange={(e) => set({ language: e.target.value as BriefLanguage })}>
                        {LANGUAGES.map((l) => (
                          <NativeSelectOption key={l} value={l}>{t(`DECISIONS.BRIEF.LANG.${l.toUpperCase()}`)}</NativeSelectOption>
                        ))}
                      </NativeSelect>
                    </SettingsField>
                    <SettingsToggleRow
                      id="brief-ai-line"
                      icon={<Sparkles />}
                      label={t('DECISIONS.BRIEF.AI_LINE')}
                      description={t('DECISIONS.BRIEF.AI_LINE_DESC')}
                      checked={draft.aiLine}
                      onCheckedChange={(v) => set({ aiLine: v })}
                    />
                    <SettingsToggleRow
                      id="brief-quiet"
                      label={t('DECISIONS.BRIEF.SKIP_QUIET')}
                      description={t('DECISIONS.BRIEF.SKIP_QUIET_DESC')}
                      checked={draft.skipWhenQuiet}
                      onCheckedChange={(v) => set({ skipWhenQuiet: v })}
                    />
                  </SettingsGroup>

                  <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-5">
                    {dirty && <span className="me-auto text-xs text-muted-foreground">{t('DECISIONS.BRIEF.UNSAVED')}</span>}
                    <Button variant="outline" disabled={!dirty || saving} onClick={() => saved && setDraft(draftOf(saved))}>
                      {t('DECISIONS.BRIEF.RESET')}
                    </Button>
                    <Button onClick={() => void save()} disabled={!dirty || saving} data-testid="brief-save">
                      {saving && <Loader2 className="animate-spin" />}
                      {t('DECISIONS.BRIEF.SAVE')}
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {last && (
              <Card className="gap-3 p-6" data-testid="brief-last-run">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-medium">{t(last.kind === 'test' ? 'DECISIONS.BRIEF.LAST.TEST_TITLE' : 'DECISIONS.BRIEF.LAST.TITLE')}</h3>
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">
                    <StatusBadge tone={STATUS_TONE[last.status]}>{t(`DECISIONS.BRIEF.LAST.${last.status.toUpperCase()}`)}</StatusBadge>
                    · <RelativeTime value={last.at} />
                  </span>
                </div>
                {last.headline && <p className="text-sm">{last.headline}</p>}
                <ul className="flex flex-col divide-y rounded-lg border">
                  {last.deliveries.map((d) => {
                    const Icon = CHANNEL_ICONS[d.kind as keyof typeof CHANNEL_ICONS] ?? Send;
                    return (
                      <li key={d.channelUuid} className="flex items-start gap-3 px-3 py-2.5 text-sm">
                        <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                        <span className="min-w-0 flex-1">{d.name}</span>
                        <span className="text-end text-xs">
                          {d.sent > 0
                            ? <StatusBadge tone="success">{t('DECISIONS.BRIEF.LAST.DELIVERED', { count: d.sent })}</StatusBadge>
                            : <StatusBadge tone={d.note === 'whatsapp_window' ? 'warning' : 'danger'}>{t(`DECISIONS.BRIEF.NOTE.${(d.note || 'failed').toUpperCase()}`)}</StatusBadge>}
                        </span>
                      </li>
                    );
                  })}
                  <li className="flex items-start gap-3 px-3 py-2.5 text-sm">
                    <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0 flex-1">{t('DECISIONS.BRIEF.EMAIL')}</span>
                    <span className="text-xs">
                      {last.email.sent > 0
                        ? <StatusBadge tone="success">{t('DECISIONS.BRIEF.LAST.EMAILED', { count: last.email.sent })}</StatusBadge>
                        : <StatusBadge tone="neutral">{t(`DECISIONS.BRIEF.NOTE.${(last.email.note || 'off').toUpperCase()}`)}</StatusBadge>}
                    </span>
                  </li>
                  {last.kind === 'scheduled' && (
                    <li className="flex items-start gap-3 px-3 py-2.5 text-sm">
                      <BellRing className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1">{t('DECISIONS.BRIEF.BELL')}</span>
                      <span className="text-xs"><StatusBadge tone={last.bell ? 'success' : 'neutral'}>{t(last.bell ? 'DECISIONS.BRIEF.LAST.IN_BELL' : 'DECISIONS.BRIEF.NOTE.OFF')}</StatusBadge></span>
                    </li>
                  )}
                </ul>
                {windowClosed && (
                  <Alert data-testid="brief-whatsapp-window">
                    <TriangleAlert />
                    <AlertTitle>{t('DECISIONS.BRIEF.WINDOW.TITLE')}</AlertTitle>
                    <AlertDescription>{t('DECISIONS.BRIEF.WINDOW.DESC')}</AlertDescription>
                  </Alert>
                )}
              </Card>
            )}
          </div>

          {/* ── Preview ── */}
          <Card className="gap-4 p-5 lg:sticky lg:top-4" data-testid="brief-preview">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-medium">{t('DECISIONS.BRIEF.PREVIEW')}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">{t('DECISIONS.BRIEF.PREVIEW_DESC')}</p>
              </div>
              <Button onClick={() => void sendTest()} disabled={testing || saving || !saved} data-testid="brief-send-test">
                {testing ? <Loader2 className="animate-spin" /> : <Send />}
                {t(testing ? 'DECISIONS.BRIEF.TESTING' : 'DECISIONS.BRIEF.TEST')}
              </Button>
            </div>
            <PillTabs value={tab} options={tabs} onChange={setTab} ariaLabel={t('DECISIONS.BRIEF.PREVIEW') as string} className="self-start" />
            {previewFailed && <p className="text-sm text-destructive">{t('DECISIONS.BRIEF.PREVIEW_FAILED')}</p>}
            {!preview && !previewFailed && <Skeleton className="h-[480px] rounded-2xl" />}
            {preview && (
              <>
                {preview.brief.quiet && <p className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">{t('DECISIONS.BRIEF.QUIET')}</p>}
                {tab === 'whatsapp' && <ChatPreview app="whatsapp" messages={preview.channels.whatsapp} channelName={firstChosen('whatsapp')?.name} time={`${pad(saved?.hour ?? 8)}:${pad(saved?.minute ?? 0)}`} />}
                {tab === 'telegram' && <ChatPreview app="telegram" messages={preview.channels.telegram} channelName={firstChosen('telegram')?.name} time={`${pad(saved?.hour ?? 8)}:${pad(saved?.minute ?? 0)}`} />}
                {tab === 'email' && <EmailPreview subject={preview.email.subject} html={preview.email.html} />}
                {preview.brief.line && preview.lineSource !== 'off' && (
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <Sparkles className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                    {t(preview.lineSource === 'fallback' ? 'DECISIONS.BRIEF.LINE_FALLBACK' : 'DECISIONS.BRIEF.LINE_MODEL')}
                  </p>
                )}
              </>
            )}
          </Card>
        </div>
      </div>
    </PageContainer>
  );
};

export default MorningBriefPage;
